import { z } from "zod";
import {
  CARD_FONTS,
  RANK_CARD_LAYOUTS,
  CARD_MAX_ELEMENTS,
  CARD_MAX_PICTURES,
  CARD_MODES,
  canonicalJson,
  cardBackgroundSchema,
  cardColourSchema,
  cardElementIdSchema,
  cardGradientSchema,
  cardHexSchema,
  cardOfLook,
  cardPictureSchema,
  cardShapeImageSchema,
  cardTextRunSchema,
  drawsPicture,
  relaidCard,
} from "./card-design.ts";
import type {
  CardBackground,
  CardColour,
  CardDesign,
  CardElement,
  CardElementKind,
  CardElementOf,
  CardFont,
  CardShapeImage,
  RankCardLayout,
  CardMode,
} from "./card-design.ts";

export const MEMBER_CARD_PARTS = [
  "layout",
  "accent",
  "mode",
  "colours",
  "font",
  "background",
  "pictures",
  "texts",
  "fills",
] as const;

export type MemberCardPart = (typeof MEMBER_CARD_PARTS)[number];

const LEGACY_PARTS: ReadonlyMap<unknown, readonly MemberCardPart[]> = new Map([
  ["style", ["layout"]],
  ["theme", ["accent", "mode"]],
]);

const currentParts = (stored: unknown): unknown => {
  if (!Array.isArray(stored)) return stored;
  const parts = (stored as readonly unknown[]).flatMap((part): readonly unknown[] => {
    const became = LEGACY_PARTS.get(part);
    return became === undefined ? [part] : became;
  });
  return [...new Set(parts)];
};

export const memberCardPartsSchema = z.preprocess(
  currentParts,
  z
    .array(z.enum(MEMBER_CARD_PARTS))
    .max(MEMBER_CARD_PARTS.length)
    .refine((parts) => new Set(parts).size === parts.length, {
      message: "names a part twice",
    }),
);

export const memberCardsSchema = z.strictObject({ allow: memberCardPartsSchema });

export const memberCardPath = (guildId: string): string => `/m/${guildId}/card`;

export type MemberCards = z.infer<typeof memberCardsSchema>;

export const memberCardPartsHeld = (
  everyone: readonly MemberCardPart[],
  rewards: readonly (readonly MemberCardPart[])[],
): readonly MemberCardPart[] => {
  const held = new Set([...everyone, ...rewards.flat()]);
  return MEMBER_CARD_PARTS.filter((part) => held.has(part));
};

const runIndexSchema = z.string().regex(/^[0-3]$/);

const byPart = <T extends z.ZodType>(value: T) =>
  z
    .record(cardElementIdSchema, value)
    .refine((entries) => Object.keys(entries).length <= CARD_MAX_ELEMENTS, {
      message: `names at most ${String(CARD_MAX_ELEMENTS)} parts`,
    });

const COLOUR_FIELDS = {
  picture: [],
  avatar: ["borderColour"],
  text: [],
  progress: ["fill", "gradient", "track"],
  stats: ["labelColour", "valueColour"],
  shape: ["fill", "gradient", "borderColour"],
} as const satisfies Readonly<Record<CardElementKind, readonly string[]>>;

type ColourField = (typeof COLOUR_FIELDS)[CardElementKind][number];

const partColoursSchema = z.strictObject({
  fill: cardColourSchema.optional(),
  gradient: cardGradientSchema.optional(),
  track: cardColourSchema.optional(),
  borderColour: cardColourSchema.optional(),
  labelColour: cardColourSchema.optional(),
  valueColour: cardColourSchema.optional(),
  runs: z.record(runIndexSchema, cardColourSchema).optional(),
});

type PartColours = z.infer<typeof partColoursSchema>;

const plainBackgroundSchema = cardBackgroundSchema.refine(
  (background) => background.kind !== "image",
  { message: "a colour background is a colour or a gradient" },
);

export const memberCardChangesSchema = z.strictObject({
  layout: z.enum(RANK_CARD_LAYOUTS).optional(),
  accent: cardHexSchema.optional(),
  mode: z.enum(CARD_MODES).optional(),
  colours: z
    .strictObject({
      accent: cardHexSchema.optional(),
      text: cardHexSchema.optional(),
      background: plainBackgroundSchema.optional(),
      parts: byPart(partColoursSchema).optional(),
    })
    .optional(),
  font: z
    .strictObject({
      card: z.enum(CARD_FONTS).optional(),
      parts: byPart(z.enum(CARD_FONTS).nullable()).optional(),
    })
    .optional(),
  background: cardBackgroundSchema.optional(),
  pictures: z.array(cardPictureSchema).max(CARD_MAX_PICTURES).optional(),
  texts: byPart(z.record(runIndexSchema, cardTextRunSchema.shape.text)).optional(),
  fills: byPart(cardShapeImageSchema.nullable()).optional(),
});

export type MemberCardChanges = z.infer<typeof memberCardChangesSchema>;

export const currentMemberChanges = (stored: unknown): unknown => {
  if (stored === null || typeof stored !== "object" || Array.isArray(stored)) return stored;
  const { style: _style, theme: _theme, ...rest } = stored as Record<string, unknown>;
  return rest;
};

const MEMBER_DESIGN_REFUSALS = ["outside_allow", "foreign_picture"] as const;

export type MemberDesignRefusal = (typeof MEMBER_DESIGN_REFUSALS)[number];

const changedParts = (changes: MemberCardChanges): readonly MemberCardPart[] =>
  MEMBER_CARD_PARTS.filter((part) => changes[part] !== undefined);

const memberChangesPictures = (changes: MemberCardChanges): readonly string[] => [
  ...(changes.background !== undefined && changes.background.kind === "image"
    ? [changes.background.assetId]
    : []),
  ...(changes.pictures === undefined ? [] : changes.pictures.map((picture) => picture.assetId)),
  ...(changes.fills === undefined
    ? []
    : Object.values(changes.fills).flatMap((fill) => (fill === null ? [] : [fill.assetId]))),
];

export const memberChangesRefusal = (
  changes: MemberCardChanges,
  allow: readonly MemberCardPart[],
  uploads: ReadonlySet<string>,
): MemberDesignRefusal | null => {
  if (changedParts(changes).some((part) => !allow.includes(part))) return "outside_allow";
  return memberChangesPictures(changes).every((assetId) => uploads.has(assetId))
    ? null
    : "foreign_picture";
};

export const memberChangesSaved = (
  stored: MemberCardChanges | null,
  sent: MemberCardChanges,
  allow: readonly MemberCardPart[],
): MemberCardChanges => {
  const kept: Record<string, unknown> = {};
  if (stored !== null) {
    for (const part of changedParts(stored)) {
      if (!allow.includes(part)) kept[part] = stored[part];
    }
  }
  return { ...(kept as MemberCardChanges), ...sent };
};

export const memberChangesShowing = (
  changes: MemberCardChanges,
  shown: (assetId: string) => boolean,
): MemberCardChanges => {
  const { background, pictures, fills, ...rest } = changes;
  const kept = pictures === undefined ? [] : pictures.filter((part) => shown(part.assetId));
  const filled =
    fills === undefined
      ? []
      : Object.entries(fills).filter(([, fill]) => fill === null || shown(fill.assetId));
  return {
    ...rest,
    ...(background === undefined || (background.kind === "image" && !shown(background.assetId))
      ? {}
      : { background }),
    ...(kept.length === 0 ? {} : { pictures: kept }),
    ...(filled.length === 0 ? {} : { fills: Object.fromEntries(filled) }),
  };
};

const withColours = (element: CardElement, colours: PartColours | undefined): CardElement => {
  if (colours === undefined) return element;
  const changed: Record<string, unknown> = { ...element };
  for (const field of COLOUR_FIELDS[element.kind] as readonly ColourField[]) {
    const colour = colours[field];
    if (colour !== undefined) changed[field] = colour;
  }
  if (element.kind === "text" && colours.runs !== undefined) {
    const runs = colours.runs;
    changed.runs = element.runs.map((run, index) => {
      const colour = runs[String(index)];
      return colour === undefined ? run : { ...run, colour };
    });
  }
  return changed as CardElement;
};

const withFont = (element: CardElement, font: CardFont | null | undefined): CardElement => {
  if (font === undefined || (element.kind !== "text" && element.kind !== "stats")) return element;
  const { font: _, ...rest } = element;
  return (font === null ? rest : { ...rest, font }) as CardElement;
};

const withWords = (
  element: CardElement,
  words: Readonly<Record<string, string>> | undefined,
): CardElement => {
  if (words === undefined || element.kind !== "text") return element;
  return {
    ...element,
    runs: element.runs.map((run, index) => {
      const text = words[String(index)];
      return text === undefined ? run : { ...run, text };
    }),
  };
};

const withPictures = (
  elements: readonly CardElement[],
  pictures: readonly CardElementOf<"picture">[],
): readonly CardElement[] => {
  const taken = new Set(elements.map((element) => element.id));
  let placed = elements.filter(drawsPicture).length;
  const added: CardElement[] = [];
  for (const picture of pictures) {
    if (elements.length + added.length >= CARD_MAX_ELEMENTS || placed >= CARD_MAX_PICTURES) break;
    let id = picture.id;
    for (let n = 2; taken.has(id); n += 1) id = `${picture.id.slice(0, 28)}-${String(n)}`;
    taken.add(id);
    placed += 1;
    added.push({ ...picture, id });
  }
  return [...elements, ...added];
};

const withFill = (element: CardElement, fill: CardShapeImage | null | undefined): CardElement => {
  if (fill === undefined || element.kind !== "shape") return element;
  const { image: _, ...rest } = element;
  return fill === null ? rest : { ...rest, image: fill };
};

const entry = <T>(map: Readonly<Record<string, T>> | undefined, key: string): T | undefined =>
  map === undefined ? undefined : map[key];

const withFills = (
  elements: readonly CardElement[],
  fills: Readonly<Record<string, CardShapeImage | null>>,
): readonly CardElement[] => {
  const unfilled = elements.map((element) =>
    entry(fills, element.id) === null ? withFill(element, null) : element,
  );
  let drawing = unfilled.filter(drawsPicture).length;
  return unfilled.map((element) => {
    const fill = entry(fills, element.id);
    if (fill === undefined || fill === null || element.kind !== "shape") return element;
    if (!drawsPicture(element)) {
      if (drawing >= CARD_MAX_PICTURES) return element;
      drawing += 1;
    }
    return withFill(element, fill);
  });
};

export const withMemberChanges = (
  server: CardDesign,
  changes: MemberCardChanges,
  allow: readonly MemberCardPart[],
): CardDesign => {
  const allowed = (part: MemberCardPart): boolean => allow.includes(part);
  let design: CardDesign = JSON.parse(JSON.stringify(server)) as CardDesign;
  const layout = allowed("layout") ? changes.layout : undefined;
  const mode = allowed("mode") ? changes.mode : undefined;
  if (layout !== undefined || mode !== undefined) {
    design = relaidCard(design, {
      ...(layout === undefined ? {} : { layout }),
      ...(mode === undefined ? {} : { mode }),
    });
  }
  if (allowed("accent") && changes.accent !== undefined) {
    design = { ...design, accent: changes.accent };
  }
  const { colours, font, texts } = changes;
  if (allowed("colours") && colours !== undefined) {
    design = {
      ...design,
      ...(colours.accent === undefined ? {} : { accent: colours.accent }),
      ...(colours.text === undefined ? {} : { text: colours.text }),
      ...(colours.background === undefined || design.background.kind === "image"
        ? {}
        : { background: colours.background }),
      elements: design.elements.map((element) =>
        withColours(element, entry(colours.parts, element.id)),
      ),
    };
  }
  if (allowed("font") && font !== undefined) {
    design = {
      ...design,
      ...(font.card === undefined ? {} : { font: font.card }),
      elements: design.elements.map((element) => withFont(element, entry(font.parts, element.id))),
    };
  }
  if (allowed("background") && changes.background !== undefined) {
    design = { ...design, background: changes.background };
  }
  if (allowed("texts") && texts !== undefined) {
    design = {
      ...design,
      elements: design.elements.map((element) => withWords(element, entry(texts, element.id))),
    };
  }
  if (allowed("fills") && changes.fills !== undefined) {
    design = { ...design, elements: [...withFills(design.elements, changes.fills)] };
  }
  if (allowed("pictures") && changes.pictures !== undefined) {
    design = { ...design, elements: [...withPictures(design.elements, changes.pictures)] };
  }
  return design;
};

const differs = (left: unknown, right: unknown): boolean =>
  canonicalJson(left) !== canonicalJson(right);

const partChanges = (
  base: CardElement,
  drawn: CardElement,
): {
  colours: PartColours | null;
  font: CardFont | null | undefined;
  words: Record<string, string> | null;
  fill: CardShapeImage | null | undefined;
} => {
  const colours: Record<string, unknown> = {};
  for (const field of COLOUR_FIELDS[drawn.kind] as readonly ColourField[]) {
    const was = (base as Partial<Record<ColourField, unknown>>)[field];
    const now = (drawn as Partial<Record<ColourField, unknown>>)[field];
    if (now !== undefined && differs(now, was)) colours[field] = now;
  }
  const runColours: Record<string, CardColour> = {};
  const words: Record<string, string> = {};
  if (base.kind === "text" && drawn.kind === "text") {
    drawn.runs.forEach((run, index) => {
      const was = base.runs[index];
      if (was === undefined) return;
      if (run.colour !== was.colour) runColours[String(index)] = run.colour;
      if (run.text !== was.text) words[String(index)] = run.text;
    });
  }
  if (Object.keys(runColours).length > 0) colours.runs = runColours;
  const fonted = (part: CardElement): CardFont | undefined =>
    part.kind === "text" || part.kind === "stats" ? part.font : undefined;
  const font = fonted(base) === fonted(drawn) ? undefined : (fonted(drawn) ?? null);
  const filled = (part: CardElement): CardShapeImage | undefined =>
    part.kind === "shape" ? part.image : undefined;
  const fill = differs(filled(base), filled(drawn)) ? (filled(drawn) ?? null) : undefined;
  return {
    colours: Object.keys(colours).length === 0 ? null : (colours as PartColours),
    font,
    words: Object.keys(words).length === 0 ? null : words,
    fill,
  };
};

const changesFrom = (
  base: CardDesign,
  drawn: CardDesign,
  allowed: (part: MemberCardPart) => boolean,
): MemberCardChanges => {
  const ids = new Set(base.elements.map((element) => element.id));
  const parts = new Map(base.elements.map((element) => [element.id, element]));
  const colourParts: Record<string, PartColours> = {};
  const fontParts: Record<string, CardFont | null> = {};
  const texts: Record<string, Record<string, string>> = {};
  const fills: Record<string, CardShapeImage | null> = {};
  for (const element of drawn.elements) {
    const was = parts.get(element.id);
    if (was === undefined || was.kind !== element.kind) continue;
    const changed = partChanges(was, element);
    if (changed.colours !== null) colourParts[element.id] = changed.colours;
    if (changed.font !== undefined) fontParts[element.id] = changed.font;
    if (changed.words !== null) texts[element.id] = changed.words;
    if (changed.fill !== undefined) fills[element.id] = changed.fill;
  }
  const plain = (
    background: CardBackground,
  ): background is Exclude<CardBackground, { kind: "image" }> => background.kind !== "image";
  const wanted = drawn.background;
  const colourBackground =
    allowed("colours") &&
    plain(base.background) &&
    plain(wanted) &&
    differs(base.background, wanted)
      ? wanted
      : undefined;
  const colours = {
    ...(drawn.accent === base.accent ? {} : { accent: drawn.accent }),
    ...(drawn.text === base.text ? {} : { text: drawn.text }),
    ...(colourBackground === undefined ? {} : { background: colourBackground }),
    ...(Object.keys(colourParts).length === 0 ? {} : { parts: colourParts }),
  };
  const font = {
    ...(drawn.font === base.font ? {} : { card: drawn.font }),
    ...(Object.keys(fontParts).length === 0 ? {} : { parts: fontParts }),
  };
  const backgroundAfterColours =
    colourBackground === undefined ? base.background : colourBackground;
  const pictures = drawn.elements.filter(
    (element): element is CardElementOf<"picture"> =>
      element.kind === "picture" && !ids.has(element.id),
  );
  return {
    ...(allowed("colours") && Object.keys(colours).length > 0 ? { colours } : {}),
    ...(allowed("font") && Object.keys(font).length > 0 ? { font } : {}),
    ...(allowed("background") && differs(backgroundAfterColours, drawn.background)
      ? { background: drawn.background }
      : {}),
    ...(allowed("texts") && Object.keys(texts).length > 0 ? { texts } : {}),
    ...(allowed("fills") && Object.keys(fills).length > 0 ? { fills } : {}),
    ...(allowed("pictures") && pictures.length > 0 ? { pictures } : {}),
  };
};

export const memberChangesOf = (
  server: CardDesign,
  drawn: CardDesign,
  allow: readonly MemberCardPart[],
): MemberCardChanges | null => {
  const allowed = (part: MemberCardPart): boolean => allow.includes(part);
  const wanted = canonicalJson(drawn);
  const framed = (frame: Pick<CardDesign, "width" | "height" | "radius" | "elements">): boolean => {
    const ids = frame.elements.map((element) => element.id);
    return (
      frame.width === drawn.width &&
      frame.height === drawn.height &&
      frame.radius === drawn.radius &&
      canonicalJson(drawn.elements.slice(0, ids.length).map((element) => element.id)) ===
        canonicalJson(ids)
    );
  };
  const layouts: readonly (RankCardLayout | undefined)[] = [
    ...(framed(server) ? [undefined] : []),
    ...(allowed("layout")
      ? RANK_CARD_LAYOUTS.filter((layout) =>
          framed(cardOfLook({ layout, accent: drawn.accent, mode: "dark" })),
        )
      : []),
  ];
  const modes: readonly (CardMode | undefined)[] = [
    undefined,
    ...(allowed("mode") ? CARD_MODES : []),
  ];
  let best: { readonly changes: MemberCardChanges; readonly length: number } | null = null;
  for (const layout of layouts) {
    for (const mode of modes) {
      const framing: MemberCardChanges = {
        ...(layout === undefined ? {} : { layout }),
        ...(mode === undefined ? {} : { mode }),
      };
      const laid = withMemberChanges(server, framing, allow);
      const accent =
        allowed("accent") && laid.accent !== drawn.accent ? { accent: drawn.accent } : {};
      const base = { ...laid, ...accent };
      const changes = { ...framing, ...accent, ...changesFrom(base, drawn, allowed) };
      if (canonicalJson(withMemberChanges(server, changes, allow)) !== wanted) continue;
      const length = canonicalJson(changes).length;
      if (best === null || length < best.length) best = { changes, length };
    }
  }
  return best === null ? null : best.changes;
};

export const MEMBER_CARD_LOCKS = [
  "not_granted",
  "leveling_off",
  "members_off",
  "reward_needed",
] as const;

export type MemberCardLock = (typeof MEMBER_CARD_LOCKS)[number];
